import { PayloadAction } from '@reduxjs/toolkit';
import dotPropImmutable from 'dot-prop-immutable';
import moment from 'moment';
import { createIntl } from 'react-intl';
import { IntegrationProvider } from 'api/integrations';
import { DEFAULT_DATE_FORMAT, MemberType } from 'api/models';
import { TaskEffortMeta, TaskEffortSetting } from 'api/tasks';
import { toastService } from 'components/molecules/toast';
import { PhaseEntityType, converToEntityId } from 'data/projects/phase-entity';
import { DateShiftUpdatesForTask } from 'utils/date-shift';
import { getTranslationCache } from 'utils/translation-loader';
import { UpdateJiraMappingsInTemplateAction } from '../actions';
import {
  ProjectTemplateTask,
  ProjectTemplateProjectPhase,
  ProjectTemplateStore,
} from '../types';
import { insert } from '../utils';

const intl = createIntl({
  locale: 'en',
  messages: getTranslationCache().get('en') || {},
});

function deleteSubTasks(
  subTasks: ProjectTemplateTask[],
  state: ProjectTemplateStore,
): ProjectTemplateStore {
  return subTasks
    ? subTasks.reduce((intermediateState, taskId) => {
        const children = dotPropImmutable.get(
          intermediateState,
          `data.${state.activeId}.tasks.${taskId}`,
        );

        let updatedState = intermediateState;

        if (children.subTasks.length > 0) {
          updatedState = deleteSubTasks(children.subTasks, updatedState);
        }

        updatedState = {
          ...updatedState,
          ...dotPropImmutable.delete(
            updatedState,
            `data.${state.activeId}.tasks.${taskId}`,
          ),
        };

        return updatedState;
      }, state)
    : state;
}

export const removeTaskFromDependencies = (
  taskId: number,
  state: ProjectTemplateStore,
): ProjectTemplateStore => {
  const tasks: ProjectTemplateTask[] = Object.values(
    dotPropImmutable.get(state, `data.${state.activeId}.tasks`),
  );

  return tasks.reduce((_state, task) => {
    if (task.taskId === taskId) {
      return _state;
    }

    if (
      !task.dependentTasks ||
      !task.dependentTasks.find((depTask) => depTask.taskId === taskId)
    ) {
      return _state;
    }

    return dotPropImmutable.merge(
      _state,
      `data.${state.activeId}.tasks.${task.taskId}`,
      {
        ...task,
        dependentTasks: task.dependentTasks.filter(
          (depTask) => depTask.taskId !== taskId,
        ),
      },
    );
  }, state);
};

const isLastTaskValid = (
  state: ProjectTemplateStore,
  taskIds: number[],
): boolean =>
  !dotPropImmutable.get(
    state,
    `data.${state.activeId}.tasks.${taskIds[taskIds.length - 1]}.isNew`,
    false,
  );

const getIndexToInsert = (state: ProjectTemplateStore, taskIds: number[]) =>
  isLastTaskValid(state, taskIds) ? taskIds.length : taskIds.length - 1;

const makeSubTasksAsPrivate = (
  state: ProjectTemplateStore,
  subTasks: number[],
): ProjectTemplateStore => {
  return subTasks
    ? subTasks.reduce((intermediateState, taskId) => {
        const children = dotPropImmutable.get(
          intermediateState,
          `data.${state.activeId}.tasks.${taskId}`,
        );
        let updatedState = intermediateState;

        if (children?.subTasks?.length > 0) {
          updatedState = makeSubTasksAsPrivate(updatedState, children.subTasks);
        }

        return dotPropImmutable.set(
          updatedState,
          `data.${state.activeId}.tasks.${taskId}.private`,
          true,
        );
      }, state)
    : state;
};

const makeTaskAsPrivate = (
  state: ProjectTemplateStore,
  taskId: number,
): ProjectTemplateStore => {
  const result = dotPropImmutable.set(
    state,
    `data.${state.activeId}.tasks.${taskId}.private`,
    true,
  );

  const subTasks = dotPropImmutable.get(
    result,
    `data.${state.activeId}.tasks.${taskId}.subTasks`,
    [],
  );

  return makeSubTasksAsPrivate(result, subTasks);
};

const resetStateForPrivateTask = (
  state: ProjectTemplateStore,
  taskId: number,
): ProjectTemplateStore => {
  const responsibleOn = dotPropImmutable.get(
    state,
    `data.${state.activeId}.tasks.${taskId}.responsibleOn`,
  );

  const assignee = dotPropImmutable.get(
    state,
    `data.${state.activeId}.tasks.${taskId}.assignee`,
  );

  let resetState = dotPropImmutable.set(
    state,
    `data.${state.activeId}.tasks.${taskId}.responsibleOn`,
    responsibleOn?.filter((_value) => _value !== MemberType.External),
  );

  if (assignee && assignee.teams) {
    resetState = dotPropImmutable.set(
      resetState,
      `data.${state.activeId}.tasks.${taskId}.assignee.teams`,
      assignee?.teams?.filter(
        (_team) => _team.teamType !== MemberType.External,
      ) || [],
    );
  }

  return resetState;
};

export const taskSlice = {
  createProjectTemplateTask: (
    state: ProjectTemplateStore,
    {
      payload,
    }: PayloadAction<{
      task: AtLeast<ProjectTemplateTask, 'taskId'>;
      projectPhaseId: number;
      insertAt?: {
        id: string;
        dir: 0 | 1;
      };
    }>,
  ): ProjectTemplateStore => {
    const { projectPhaseId, task, insertAt } = payload;

    const parentProjectPhase: ProjectTemplateProjectPhase =
      dotPropImmutable.get(
        state,
        `data.${state.activeId}.projectPhases.${projectPhaseId}`,
        {},
      );

    const taskPayload = {
      ...task,
      projectPhase: {
        projectPhaseId,
      },
      subTasks: [],
      startDay: parentProjectPhase.startDay || 1,
      duration: 1,
      private: parentProjectPhase.private,
      isNewEntity: true,
    };

    const taskPath = `data.${state.activeId}.tasks.${taskPayload.taskId}`;
    const taskResult = dotPropImmutable.set(state, taskPath, taskPayload);

    const taskRefPath = `data.${state.activeId}.projectPhases.${projectPhaseId}.tasks`;
    const entityOrder = dotPropImmutable.get(
      taskResult,
      `data.${state.activeId}.projectPhases.${projectPhaseId}.entityOrder`,
      [],
    );

    let result: ProjectTemplateStore;

    if (!insertAt) {
      const taskOrderResult = dotPropImmutable.merge(taskResult, taskRefPath, [
        taskPayload.taskId,
      ]);

      result = dotPropImmutable.set(
        taskOrderResult,
        `data.${state.activeId}.projectPhases.${projectPhaseId}.entityOrder`,
        [...entityOrder, converToEntityId(task.taskId, PhaseEntityType.Task)],
      );
    } else {
      const insertAtIndex = entityOrder.findIndex((id) => id === insertAt.id);
      const taskIds = dotPropImmutable.get(taskResult, taskRefPath, []);

      const modifiedTaskIds = insert(
        taskIds,
        insertAtIndex + insertAt.dir,
        task.taskId,
      );

      const taskOrderResult = dotPropImmutable.set(
        taskResult,
        taskRefPath,
        modifiedTaskIds,
      );

      const modifiedEntityOrder = insert(
        entityOrder,
        insertAtIndex + insertAt.dir,
        converToEntityId(task.taskId, PhaseEntityType.Task),
      );

      result = dotPropImmutable.set(
        taskOrderResult,
        `data.${state.activeId}.projectPhases.${projectPhaseId}.entityOrder`,
        modifiedEntityOrder,
      );
    }

    return result;
  },

  createProjectTemplateSubTask: (
    state: ProjectTemplateStore,
    {
      payload,
    }: PayloadAction<{
      task: AtLeast<ProjectTemplateTask, 'taskId'>;
      formTemplateId?: number;
      parentTaskId: number;
      insertAt?: {
        id: number;
        dir: 0 | 1;
      };
    }>,
  ): ProjectTemplateStore => {
    const { parentTaskId, task, insertAt } = payload;

    const parentTask: ProjectTemplateTask = dotPropImmutable.get(
      state,
      `data.${state.activeId}.tasks.${parentTaskId}`,
    );

    const taskPayload: Partial<ProjectTemplateTask> = {
      ...task,
      parentTask: {
        taskId: parentTaskId,
      },
      subTasks: [],
      startDay: parentTask.startDay,
      duration: parentTask.duration,
      private: parentTask.private,
      isNewEntity: true,
    };
    const taskPath = `data.${state.activeId}.tasks.${task.taskId}`;
    const taskResult = dotPropImmutable.set(state, taskPath, taskPayload);

    const taskRefPath = `data.${state.activeId}.tasks.${parentTaskId}.subTasks`;

    let result: ProjectTemplateStore;

    if (!insertAt) {
      result = dotPropImmutable.merge(taskResult, taskRefPath, [
        taskPayload.taskId,
      ]);
    } else {
      const taskIds = dotPropImmutable.get(taskResult, taskRefPath, []);

      const insertAtIndex = taskIds.findIndex((i) => i === insertAt.id);

      const modifiedTaskIds = insert(
        taskIds,
        insertAtIndex + insertAt.dir,
        payload.task.taskId,
      );

      result = dotPropImmutable.set(taskResult, taskRefPath, modifiedTaskIds);
    }

    return result;
  },

  updateProjectTemplateTask: (
    state: ProjectTemplateStore,
    {
      payload: { task, isNew, updatePrivacyOfChildren },
    }: PayloadAction<{
      task: AtLeast<ProjectTemplateTask, 'taskId'>;
      isNew?: boolean;
      updatePrivacyOfChildren?: boolean;
    }>,
  ): ProjectTemplateStore => {
    const { private: isPrivate } = task;
    const allTasksMap = dotPropImmutable.get(
      state,
      `data.${state.activeId}.tasks`,
    );
    const taskWithSameExternalId = Object.values(allTasksMap).filter(
      (e: any) =>
        e.externalRefKey &&
        e.externalRefKey === task.externalRefKey &&
        e.taskId !== task.taskId,
    );

    if (taskWithSameExternalId.length > 0) {
      toastService({
        title: intl.formatMessage({
          id: 'templates.external_ref_key_exists.title',
        }),
        message: intl.formatMessage(
          { id: 'templates.external_ref_key_exists.body' },
          {
            externalRefKey: task.externalRefKey,
          },
        ),
      }).error();

      return state;
    }

    let updatedTask = task;

    if (task.sheetTemplate) {
      updatedTask = {
        ...task,
        ...(task.sheetTemplate && {
          sheetTemplate: {
            ...task.sheetTemplate,
            assignee: task.assignee || {
              users: [],
              teams: [],
            },
          },
        }),
      };
    }
    let result = dotPropImmutable.merge(
      state,
      `data.${state.activeId}.tasks.${task.taskId}`,
      {
        ...updatedTask,
        ...{ isNew },
      },
    );

    if (isPrivate) {
      const resetState = resetStateForPrivateTask(result, task.taskId);
      const resetTask = dotPropImmutable.get(
        resetState,
        `data.${state.activeId}.tasks.${task.taskId}`,
      );

      updatedTask = {
        ...task,
        responsibleOn: resetTask.responsibleOn,
        assignee: resetTask.assignee,
      };
    }

    const { subTasks } = task;

    function setPrivate(_subTasks) {
      _subTasks?.forEach((sid) => {
        const children = dotPropImmutable.get(
          result,
          `data.${state.activeId}.tasks.${sid}`,
        );

        result = dotPropImmutable.set(
          result,
          `data.${state.activeId}.tasks.${sid}.private`,
          isPrivate,
        );

        result = isPrivate ? resetStateForPrivateTask(result, sid) : result;

        if (children.subTasks.length > 0) {
          setPrivate(children.subTasks);
        }
      });
    }
    if (updatePrivacyOfChildren === true) {
      setPrivate(subTasks);
    }

    return dotPropImmutable.merge(
      result,
      `data.${state.activeId}.tasks.${task.taskId}`,
      {
        ...updatedTask,
        ...{ isNew },
      },
    );
  },

  updateProjectTemplateTasks: (
    state: ProjectTemplateStore,
    {
      payload,
    }: PayloadAction<
      {
        id: number;
        startDay?: number;
        duration?: number;
        effort?: number;
        taskEffortMeta?: TaskEffortMeta[];
        taskEffortSetting?: TaskEffortSetting;
      }[]
    >,
  ): ProjectTemplateStore => {
    const updatedTasks = {};

    payload?.forEach(
      ({
        id,
        startDay,
        duration,
        effort,
        taskEffortMeta,
        taskEffortSetting,
      }) => {
        const task = state.data[`${state.activeId}`].tasks[id];

        const updates: Partial<ProjectTemplateTask> = {};

        if (startDay) {
          updates.startDay = startDay;
        }
        if (duration) {
          updates.duration = duration;
        }
        if (effort != null) {
          updates.effort = effort;
        }
        if (taskEffortMeta != null) {
          updates.taskEffortMeta = taskEffortMeta;
        }
        if (taskEffortSetting) {
          updates.taskEffortSetting = taskEffortSetting;
        }

        updatedTasks[id] = {
          ...task,
          ...updates,
        };
      },
    );

    return dotPropImmutable.merge(
      state,
      `data.${state.activeId}.tasks`,
      updatedTasks,
    );
  },

  makeDateShiftsForTasks: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<DateShiftUpdatesForTask[]>,
  ): ProjectTemplateStore => {
    const updatedTasks = {};

    payload?.forEach((taskUpdates) => {
      const { taskId, startDate, dueDate, dependentTasks } = taskUpdates;
      const task = state.data[`${state.activeId}`].tasks[taskId];
      const currentStartDate = moment()
        .add(task.startDay, 'day')
        .startOf('day')
        .format(DEFAULT_DATE_FORMAT);
      const currentDueDate = moment()
        .add(task.startDay + task.duration - 1, 'day')
        .endOf('day')
        .format(DEFAULT_DATE_FORMAT);

      updatedTasks[taskId] = {
        ...task,
        startDay: startDate
          ? Math.round(
              Math.abs(
                moment().startOf('day').diff(moment(startDate), 'day', true),
              ),
            )
          : task.startDay,
        duration:
          startDate || dueDate
            ? Math.round(
                moment(dueDate || currentDueDate)
                  .endOf('day')
                  .diff(
                    moment(startDate || currentStartDate).startOf('day'),
                    'day',
                    true,
                  ),
              )
            : task.duration,
        dependentTasks: dependentTasks || task.dependentTasks,
      };
    });

    return dotPropImmutable.merge(
      state,
      `data.${state.activeId}.tasks`,
      updatedTasks,
    );
  },

  updateProjectTemplateParentOfTask: (
    state: ProjectTemplateStore,
    {
      payload,
    }: PayloadAction<{
      task: ProjectTemplateTask;
      projectPhase?: Pick<ProjectTemplateProjectPhase, 'projectPhaseId'>;
      parentTask?: Pick<ProjectTemplateTask, 'taskId'>;
      insertAt?: number;
    }>,
  ): ProjectTemplateStore => {
    let result;

    if (payload.projectPhase) {
      const parentPhase = dotPropImmutable.get(
        state,
        `data.${state.activeId}.projectPhases.${payload.projectPhase.projectPhaseId}`,
        {},
      );

      /**
       * Update parent reference in the task.
       */
      result = dotPropImmutable.merge(
        state,
        `data.${state.activeId}.tasks.${payload.task.taskId}`,
        {
          ...payload.task,
          projectPhase: payload.projectPhase,
          parentTask: undefined,
        },
      );

      if (parentPhase.private === true) {
        result = makeTaskAsPrivate(result, payload.task.taskId);
      }

      /**
       * Remove task reference from existing parent (either phase or parent task).
       */
      let taskRefPath = `data.${state.activeId}.projectPhases.${payload.projectPhase.projectPhaseId}.tasks`;
      let taskIds = dotPropImmutable.get(result, taskRefPath, []);

      if (payload.task.projectPhase) {
        taskRefPath = `data.${state.activeId}.projectPhases.${payload.task.projectPhase.projectPhaseId}.tasks`;
        taskIds = dotPropImmutable
          .get(result, taskRefPath, [])
          .filter((id) => id !== payload.task.taskId);

        result = dotPropImmutable.set(result, taskRefPath, taskIds);
      } else if (payload.task.parentTask) {
        taskRefPath = `data.${state.activeId}.tasks.${payload.task.parentTask.taskId}.subTasks`;
        taskIds = dotPropImmutable
          .get(result, taskRefPath, [])
          .filter((id) => id !== payload.task.taskId);

        result = dotPropImmutable.set(result, taskRefPath, taskIds);
      }

      /**
       * Add task reference to the new parent.
       */
      taskIds = dotPropImmutable.get(
        result,
        `data.${state.activeId}.projectPhases.${payload.projectPhase.projectPhaseId}.tasks`,
        [],
      );

      const indexToAdd =
        payload.insertAt == null
          ? getIndexToInsert(result, taskIds)
          : payload.insertAt;

      result = dotPropImmutable.set(
        result,
        `data.${state.activeId}.projectPhases.${payload.projectPhase.projectPhaseId}.tasks`,
        insert(taskIds, indexToAdd, payload.task.taskId),
      );
    } else if (payload.parentTask) {
      const parentTask = dotPropImmutable.get(
        state,
        `data.${state.activeId}.tasks.${payload.parentTask.taskId}`,
        {},
      );

      result = dotPropImmutable.merge(
        state,
        `data.${state.activeId}.tasks.${payload.task.taskId}`,
        {
          ...payload.task,
          parentTask: payload.parentTask,
          projectPhase: undefined,
        },
      );
      if (parentTask.private === true) {
        result = makeTaskAsPrivate(result, payload.task.taskId);
      }

      let taskRefPath = `data.${state.activeId}.tasks.${payload.parentTask.taskId}.subTasks`;
      let taskIds = dotPropImmutable.get(result, taskRefPath, []);

      if (payload.task.parentTask) {
        taskRefPath = `data.${state.activeId}.tasks.${payload.task.parentTask.taskId}.subTasks`;
        taskIds = dotPropImmutable
          .get(result, taskRefPath, [])
          .filter((id) => id !== payload.task.taskId);

        result = dotPropImmutable.set(result, taskRefPath, taskIds);
      } else if (payload.task.projectPhase) {
        taskRefPath = `data.${state.activeId}.projectPhases.${payload.task.projectPhase.projectPhaseId}.tasks`;
        taskIds = dotPropImmutable
          .get(result, taskRefPath, [])
          .filter((id) => id !== payload.task.taskId);

        result = dotPropImmutable.set(result, taskRefPath, taskIds);
      }

      taskIds = dotPropImmutable.get(
        result,
        `data.${state.activeId}.tasks.${payload.parentTask.taskId}.subTasks`,
        [],
      );
      const indexToAdd =
        payload.insertAt == null
          ? getIndexToInsert(result, taskIds)
          : payload.insertAt;

      result = dotPropImmutable.set(
        result,
        `data.${state.activeId}.tasks.${payload.parentTask.taskId}.subTasks`,
        insert(taskIds, indexToAdd, payload.task.taskId),
      );
    } else {
      return state;
    }

    return result;
  },

  deleteProjectTemplateTask: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<AtLeast<ProjectTemplateTask, 'taskId'>>,
  ): ProjectTemplateStore => {
    const path = `data.${state.activeId}.tasks.${payload.taskId}`;
    const taskResult: ProjectTemplateTask = dotPropImmutable.get(state, path);
    const { subTasks } = taskResult;

    let result: ProjectTemplateStore = deleteSubTasks(subTasks, state);

    result = {
      ...result,
      ...dotPropImmutable.delete(
        removeTaskFromDependencies(payload.taskId, result),
        path,
      ),
    };

    const taskIds = result.data[state.activeId].projectPhases[
      taskResult.projectPhase.projectPhaseId
    ].tasks.filter((id) => (id as unknown as number) !== payload.taskId);

    result = dotPropImmutable.set(
      result,
      `data.${state.activeId}.projectPhases.${taskResult.projectPhase.projectPhaseId}.tasks`,
      taskIds,
    );

    return result;
  },

  deleteProjectTemplateSubTask: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<AtLeast<ProjectTemplateTask, 'taskId'>>,
  ): ProjectTemplateStore => {
    const path = `data.${state.activeId}.tasks.${payload.taskId}`;
    const taskResult: ProjectTemplateTask = dotPropImmutable.get(state, path);
    const { subTasks } = taskResult;

    let result: ProjectTemplateStore = deleteSubTasks(subTasks, state);

    result = {
      ...result,
      ...dotPropImmutable.delete(
        removeTaskFromDependencies(payload.taskId, result),
        path,
      ),
    };

    const taskIds = result.data[state.activeId].tasks[
      taskResult.parentTask.taskId
    ].subTasks.filter((id) => (id as unknown as number) !== payload.taskId);

    result = dotPropImmutable.set(
      result,
      `data.${state.activeId}.tasks.${taskResult.parentTask.taskId}.subTasks`,
      taskIds,
    );

    return { ...state, ...result };
  },
  addProjectTemplateTaskDependency: (
    state: ProjectTemplateStore,
    {
      payload: { taskId, dependentTaskId, dependencyType, lag },
    }: PayloadAction<{
      taskId: number;
      dependentTaskId: number;
      dependencyType: string;
      lag: number;
    }>,
  ): ProjectTemplateStore => {
    const dependentTask = dotPropImmutable.get(
      state,
      `data.${state.activeId}.tasks.${dependentTaskId}`,
      {},
    );

    // const result = dotPropImmutable.set(
    //   state,
    //   `data.${state.activeId}.tasks.${taskId}.startDay`,
    //   dependentTask.startDay + dependentTask.duration,
    // );

    return dotPropImmutable.merge(
      state,
      `data.${state.activeId}.tasks.${taskId}.dependentTasks`,
      [
        {
          taskId: dependentTask.taskId,
          taskName: dependentTask.taskName,
          dependencyType,
          lag,
        },
      ],
    );
  },

  updateProjectTemplateTaskDependencies: (
    state: ProjectTemplateStore,
    {
      payload: { taskId, dependentTasks },
    }: PayloadAction<{
      taskId: number;
      dependentTasks: {
        taskId: number;
        dependencyType: string;
        lag: number | null;
        taskName?: string;
      }[];
    }>,
  ): ProjectTemplateStore => {
    return dotPropImmutable.set(
      state,
      `data.${state.activeId}.tasks.${taskId}.dependentTasks`,
      dependentTasks,
    );
  },

  addProjectTemplatePhaseDependency: (
    state: ProjectTemplateStore,
    {
      payload: { phaseId, dependentPhaseId, dependencyType, lag },
    }: PayloadAction<{
      phaseId: number;
      dependentPhaseId: number;
      dependencyType: string;
      lag: number | null;
    }>,
  ): ProjectTemplateStore => {
    const dependentPhase = dotPropImmutable.get(
      state,
      `data.${state.activeId}.projectPhases.${dependentPhaseId}`,
      {},
    );

    return dotPropImmutable.merge(
      state,
      `data.${state.activeId}.projectPhases.${phaseId}.dependentProjectPhases`,
      [
        {
          projectPhaseId: dependentPhase.projectPhaseId,
          projectPhaseName: dependentPhase.projectPhaseName,
          dependencyType,
          lag,
        },
      ],
    );
  },

  updateProjectTemplatePhaseDependencies: (
    state: ProjectTemplateStore,
    {
      payload: { phaseId, dependentPhases },
    }: PayloadAction<{
      phaseId: number;
      dependentPhases: {
        projectPhaseId: number;
        dependencyType: string;
        lag: number | null;
        projectPhaseName?: string;
      }[];
    }>,
  ): ProjectTemplateStore => {
    return dotPropImmutable.set(
      state,
      `data.${state.activeId}.projectPhases.${phaseId}.dependentProjectPhases`,
      dependentPhases,
    );
  },

  updateJiraMappingInTemplate: (
    state: ProjectTemplateStore,
    { payload }: PayloadAction<UpdateJiraMappingsInTemplateAction>,
  ): ProjectTemplateStore => {
    const integrationResource = payload?.data
      ? [
          {
            externalIntegrationType: IntegrationProvider.Jira,
            externalResourceType: 'issue',
            externalResourceInputs: payload.data,
          },
        ]
      : [];

    return dotPropImmutable.set(
      state,
      `data.${state.activeId}.tasks.${payload.taskId}.linkedResources`,
      integrationResource,
    );
  },
};
